import {
  Component,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import {
  FilterType,
  AppliedFilterType,
  WeekType,
  OptionType,
  DateRangeType,
  TextType,
  NumberType,
} from '@app/core/interfaces/filter.interface';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { PresetOptionEnum } from '@app/core/constants/date_presets.enum';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { Ag1WeekRangeComponent } from '../../ag1-week-range/ag1-week-range.component';
import { Ag1DateRangeComponent } from '../../ag1-date-range/ag1-date-range.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-filter',
  templateUrl: './ag1-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgSelectComponent,
    FormsModule,
    Ag1WeekRangeComponent,
    Ag1DateRangeComponent,
    HumanizePipe,
  ],
})
export class Ag1FilterComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private eventBus = inject(EventBusService);

  @Input() filter: FilterType;
  @Input() appliedFilters: AppliedFilterType;
  @Input() disabled: boolean = false;
  @Input() showPresetSelection: boolean = false;

  humanizePipe = new HumanizePipe();
  private pendingAppliedFilters: AppliedFilterType = {};

  constructor() {}

  ngOnInit(): void {
    // Initialize pendingAppliedFilters from EventBus or use appliedFilters as fallback
    const pending: AppliedFilterType | undefined =
      this.eventBus.getChannelValue('pendingAppliedFilters');
    this.pendingAppliedFilters = pending
      ? structuredClone(pending)
      : structuredClone(this.appliedFilters || {});

    // Subscribe to pendingAppliedFilters changes
    this.eventBus
      .getChannel('pendingAppliedFilters')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((pendingFilters) => {
        // Clone to prevent mutations from affecting the original
        this.pendingAppliedFilters = structuredClone(pendingFilters || {});
      });
  }

  // date range
  get startDateRange(): Date {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      return null;
    }

    if (
      !(this.pendingAppliedFilters[this.filter.name] as DateRangeType)
        .startDateRange
    ) {
      return null;
    }

    // Context: appliedFilters object is linked to the url
    // Dates are stored as utc date strings in the url so need to be converted back to Dates
    const utcStartDate: string = (
      this.pendingAppliedFilters[this.filter.name] as DateRangeType
    ).startDateRange;

    return new Date(utcStartDate);
  }

  set startDateRange(value: Date) {
    this.updateDateRange(value, 'startDateRange');
  }

  get dateRangePresetOption(): PresetOptionEnum | null {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      return null;
    }

    return (this.pendingAppliedFilters[this.filter.name] as DateRangeType)
      ?.presetOption;
  }

  get endDateRange(): Date {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      return null;
    }

    if (
      !(this.pendingAppliedFilters[this.filter.name] as DateRangeType)
        .endDateRange
    ) {
      return null;
    }

    // Context: appliedFilters object is linked to the url
    // Dates are stored as utc date strings in the url so need to be converted back to Dates
    const utcEndDate: string = (
      this.pendingAppliedFilters[this.filter.name] as DateRangeType
    ).endDateRange;

    return new Date(utcEndDate);
  }

  set endDateRange(value: Date) {
    this.updateDateRange(value, 'endDateRange');
  }

  get dropdown(): Record<string, any> {
    return this.pendingAppliedFilters[this.filter?.name];
  }

  set dropdown(value: Record<string, any>) {
    if (this.filter.type === 'dropdown-single') {
      this.pendingAppliedFilters[this.filter?.name] = value
        ? ([value] as any[])
        : [];
    } else {
      this.pendingAppliedFilters[this.filter?.name] = value;
    }
    this.updateAppliedFilters();
  }

  updateDate(date: string, type: string): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }
    if (date === '') {
      delete this.pendingAppliedFilters[this.filter.name]?.[type];
    } else {
      this.pendingAppliedFilters[this.filter.name][type] = date;
    }
    this.updateAppliedFilters();
  }

  // week range
  get startWeek(): string {
    return (this.pendingAppliedFilters[this.filter.name] as WeekType)
      ?.startWeek;
  }

  set startWeek(value: string) {
    this.updateWeek(value, 'startWeek');
  }

  set endWeek(value: string) {
    this.updateWeek(value, 'endWeek');
  }

  get endWeek(): string {
    return (this.pendingAppliedFilters[this.filter.name] as WeekType)?.endWeek;
  }

  updateWeek(week: string, type: string): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }
    if (week === '') {
      delete this.pendingAppliedFilters[this.filter.name]?.[type];
    } else {
      this.pendingAppliedFilters[this.filter.name][type] = week;
    }
    this.updateAppliedFilters();
  }

  // text
  get text(): string {
    return (this.pendingAppliedFilters[this.filter.name] as TextType)?.text;
  }

  set text(value: string) {
    this.pendingAppliedFilters[this.filter?.name] = { text: value };
    this.updateText(value);
  }

  updateText(text: string): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }
    if (text === '') {
      delete this.pendingAppliedFilters[this.filter.name]?.['text'];
    } else {
      this.pendingAppliedFilters[this.filter.name] = { text };
    }
    this.updateAppliedFilters();
  }

  // number
  get number(): number {
    return (this.pendingAppliedFilters[this.filter.name] as NumberType)?.number;
  }

  set number(value: number) {
    this.pendingAppliedFilters[this.filter?.name] = { number: value };
    this.updateNumber(value);
  }

  updateNumber(number: number): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }
    if (isNaN(number)) {
      delete this.pendingAppliedFilters[this.filter.name]?.['number'];
    } else {
      this.pendingAppliedFilters[this.filter.name] = { number };
    }
    this.updateAppliedFilters();
  }

  updateDateRange(date: Date, type: string): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }
    if (date === null) {
      delete this.pendingAppliedFilters[this.filter.name]?.[type];
    } else {
      this.pendingAppliedFilters[this.filter.name][type] = date;
    }
    this.updateAppliedFilters();
  }

  clearEmptyAppliedFilters(): void {
    Object.keys(this.pendingAppliedFilters).forEach((key) => {
      if (Object.keys(this.pendingAppliedFilters[key]).length === 0) {
        delete this.pendingAppliedFilters[key];
      }
    });
  }

  updateAppliedFilters(): void {
    this.clearEmptyAppliedFilters();
    // Clone before setting to ensure immutability
    this.eventBus.setChannel(
      'pendingAppliedFilters',
      structuredClone(this.pendingAppliedFilters),
    );
  }

  addCustomItem(term: string) {
    return { id: term, label: term } as OptionType;
  }

  get dropdownPlaceholder(): string {
    // Prefer the filter's explicit displayName over a humanized name. Acronyms
    // like "PO" get destroyed by humanize ("poNumbers" → "Po Numbers"), and
    // the heading template already prefers displayName — keep the placeholder
    // consistent with that.
    const label =
      this.filter?.displayName ??
      this.humanizePipe.transform(this.filter?.name);
    // can add item
    if (this.filter?.canAddItem) {
      // no items present
      if (!this.filter?.options?.length) {
        return 'Add ' + label;
      }

      // items present
      return 'Add or select ' + label;
    }

    // cannot add item
    return 'Select one or more ' + label;
  }

  get dropdownPlaceHolderSingle(): string {
    // can add item
    if (this.filter?.canAddItem) {
      // no items present
      if (!this.filter?.options?.length) {
        return 'Add a ' + this.filter?.name;
      }

      // items present
      return 'Add or select a ' + this.filter?.name;
    }

    // cannot add item
    return 'Select a ' + this.filter?.name;
  }

  onDateRangePresetOptionChange(presetOption: PresetOptionEnum | null): void {
    if (!this.pendingAppliedFilters[this.filter.name]) {
      this.pendingAppliedFilters[this.filter.name] = {};
    }

    if (presetOption === null || presetOption === undefined) {
      delete this.pendingAppliedFilters[this.filter.name]['presetOption'];
    } else {
      this.pendingAppliedFilters[this.filter.name]['presetOption'] =
        presetOption;
    }

    this.updateAppliedFilters();
  }
}
